/**
 * Global behavior listeners — click, scroll depth, form.
 * Passive/throttled to avoid performance impact. Single init from BehaviorTrackingProvider.
 */

import { trackClick, trackScrollDepth, trackFormInteraction, resetScrollDepthForPage } from './behaviorTracking'
import { getTrackingContext } from './trackingContextStore'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function throttle<A extends unknown[]>(fn: (...args: A) => void, ms: number): (...args: A) => void {
  let last = 0
  return (...args: A) => {
    const now = Date.now()
    if (now - last >= ms) {
      last = now
      fn(...args)
    }
  }
}

function getClickElementKey(target: EventTarget | null): string | null {
  if (!target || !(target instanceof HTMLElement)) return null
  const el = target.closest('[data-track-id]') as HTMLElement | null
  if (el?.dataset.trackId) return el.dataset.trackId

  const analyticsId = target.closest('[data-analytics-id]') as HTMLElement | null
  if (analyticsId?.dataset.analyticsId) return analyticsId.dataset.analyticsId

  const role = target.getAttribute('role')
  const ariaLabel = target.getAttribute('aria-label')
  const name = [role, ariaLabel].filter(Boolean).join('_')
  if (name) return name.replace(/\s+/g, '_').slice(0, 80)

  const tag = target.tagName.toLowerCase()
  const id = target.id
  const text = (target.textContent ?? '').trim().slice(0, 30).replace(/\s+/g, '_')
  if (tag === 'button' || tag === 'a') return [tag, id || text].filter(Boolean).join('_') || tag
  if (id) return id
  if (text) return `${tag}_${text}`

  return tag
}

// ─── Click ────────────────────────────────────────────────────────────────────

const CLICK_THROTTLE_MS = 300

function handleClick(e: MouseEvent): void {
  const key = getClickElementKey(e.target)
  if (!key) return

  const label =
    (e.target instanceof HTMLElement &&
      (e.target.closest('[data-track-id]') as HTMLElement)?.dataset.trackId) ||
    (e.target as HTMLElement).getAttribute('aria-label') ||
    (e.target as HTMLElement).textContent?.trim().slice(0, 50) ||
    key

  trackClick(key, label, {
    path: window.location.pathname,
  })
}

// ─── Scroll depth ─────────────────────────────────────────────────────────────

const SCROLL_THROTTLE_MS = 200
const DEPTHS = [25, 50, 75, 100]

function getScrollDepth(): number {
  const { scrollTop, scrollHeight, clientHeight } = document.documentElement
  if (scrollHeight <= clientHeight) return 100
  const scrollable = scrollHeight - clientHeight
  const pct = Math.round((scrollTop / scrollable) * 100)
  return Math.min(100, pct)
}

function getScrollDepthMilestone(current: number): number | null {
  for (const d of DEPTHS) {
    if (current >= d) return d
  }
  return null
}

let lastScrollDepth = 0

function handleScroll(): void {
  const depth = getScrollDepth()
  const milestone = getScrollDepthMilestone(depth)
  if (milestone === null || milestone === lastScrollDepth) return
  lastScrollDepth = milestone

  const pageKey = window.location.pathname || 'unknown'
  trackScrollDepth(pageKey, milestone, { path: pageKey })
}

// ─── Form ─────────────────────────────────────────────────────────────────────

function handleFormSubmit(e: Event): void {
  const form = e.target instanceof HTMLFormElement ? e.target : (e.target as HTMLElement).closest('form')
  if (!form) return
  const formId = form.id || form.getAttribute('name') || 'anonymous'
  trackFormInteraction(formId, 'submit', { path: window.location.pathname })
}

function handleFormFocus(e: FocusEvent): void {
  const target = e.target
  if (!(target instanceof HTMLFormElement) && !(target instanceof HTMLInputElement) && !(target instanceof HTMLTextAreaElement) && !(target instanceof HTMLSelectElement)) return
  const form = target instanceof HTMLFormElement ? target : target.closest('form')
  if (!form) return
  const formId = form.id || form.getAttribute('name') || 'anonymous'
  const field = target instanceof HTMLFormElement ? null : (target as HTMLInputElement).name || (target as HTMLInputElement).id
  trackFormInteraction(formId, 'focus', { path: window.location.pathname, field: field ?? undefined })
}

// ─── Public API ───────────────────────────────────────────────────────────────

let listenersAttached = false

/**
 * Attach global behavior listeners. Call once from BehaviorTrackingProvider.
 * Uses passive scroll and throttling to minimize performance impact.
 */
export function setupBehaviorListeners(): void {
  if (listenersAttached) return
  listenersAttached = true

  document.addEventListener('click', throttle(handleClick, CLICK_THROTTLE_MS), true)

  window.addEventListener(
    'scroll',
    throttle(handleScroll, SCROLL_THROTTLE_MS),
    { passive: true }
  )

  document.addEventListener('submit', handleFormSubmit, true)
  document.addEventListener('focusin', throttle(handleFormFocus, 400), true)

  if (import.meta.env.DEV) {
    console.info('[BehaviorTracking] Global listeners attached (click, scroll, form)')
  }
}

/**
 * Call when route changes so scroll depth is reset for the new page.
 */
export function onRouteChangeForScroll(pathname: string): void {
  lastScrollDepth = 0
  resetScrollDepthForPage(pathname)
}
